import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Spacer,
  Text,
  Table,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Td,
  Tr,
  useToast,
  HStack,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { date as commonDate } from '@finley-technologies/common';

import {
  useDeleteAssignmentStrategyMutation,
  useGetAssignmentStrategiesForImplementationQuery,
  useUpdateAssignmentStrategiesDefaultMutation,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { AssignmentSkeletonLoadingTable } from '../AssignmentSkeletonLoadingTables';
import { AssignmentStrategy } from '../types';
import { DeleteAssignmentStrategyButton } from './DeleteAssignmentStrategyButton';
import { colors } from 'packages/common-frontend/colors';
import { successToastConfig } from 'packages/common-frontend/constants';
import { useLatestAssignmentPipelineRunMetadataForImplementationContext } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForImplementationProvider';
import { PipelineStatusAlert } from 'packages/loanProduct/ui/v2/PipelineStatusAlert';
import { FiChevronRight } from 'react-icons/fi';

export const AssignmentSelectStrategyTable = ({
  isEditable,
  assignmentStrategies,
  refetchStrategies,
  implementationId,
}: {
  isEditable: boolean;
  assignmentStrategies: AssignmentStrategy[];
  refetchStrategies: () => void;
  implementationId: string;
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [
    updateAssignmentStrategiesDefault,
  ] = useUpdateAssignmentStrategiesDefaultMutation({
    onCompleted: ({ updateAssignmentStrategiesDefault: response }) => {
      const { id, name, default: isDefault } = response;
      successToastConfig.id = _.uniqueId(
        `${id}-updateAssignmentStrategiesDefault-toast`,
      );
      successToastConfig.title = `Successfully ${
        isDefault ? 'set' : 'removed'
      } ${name} as a default assignment strategy`;
      toast(successToastConfig);
      refetchStrategies();
    },
  });

  const [
    deleteAssignmentStrategy,
    { loading: deleteAssignmentStrategyLoading },
  ] = useDeleteAssignmentStrategyMutation({
    onCompleted: ({ deleteAssignmentStrategy: res }) => {
      const { id, name } = res;
      successToastConfig.id = _.uniqueId(
        `${id}-deleteAssignmentStrategy-toast`,
      );
      successToastConfig.title = `Successfully deleted the ${name} assignment strategy`;
      toast(successToastConfig);
      refetchStrategies();
    },
  });

  return (
    <Box mb="32px">
      <TableContainer borderWidth="1px">
        <Table>
          <Thead>
            <Tr>
              {isEditable && <Th width="5%">Set Defaults</Th>}
              <Th width="30%">Assignment Strategy Name</Th>
              <Th width="15%">Updated At</Th>
              <Th width="15%">Updated By</Th>
              <Th width="30%">Description</Th>
              <Th width="5%">{isEditable ? 'Actions' : ''}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {assignmentStrategies.length === 0 ? (
              <Tr>
                <Td>
                  <Text color="gray">A strategy has not been created yet.</Text>
                </Td>
              </Tr>
            ) : (
              _.map(
                assignmentStrategies,
                ({
                  id,
                  name,
                  default: isDefault,
                  description,
                  updatedByEmail,
                  updated_at: updatedAt,
                }) => {
                  return (
                    <Tr
                      key={id}
                      onClick={() => {
                        if (!isEditable) {
                          navigate(
                            `/assignments/${implementationId}/strategy/${id}/apply`,
                          );
                        }
                      }}
                      _hover={{ cursor: isEditable ? 'auto' : 'pointer' }}
                    >
                      {isEditable && (
                        <Td
                          textAlign="center"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          _hover={{ cursor: 'auto' }}
                        >
                          <Checkbox
                            onChange={() => {
                              updateAssignmentStrategiesDefault({
                                variables: {
                                  assignmentStrategyId: id,
                                  isDefault: !isDefault,
                                },
                              });
                            }}
                            isChecked={isDefault}
                          />
                        </Td>
                      )}
                      <Td minWidth="20vw" whiteSpace="pre-wrap">
                        {name}
                      </Td>
                      <Td>
                        {commonDate.dateToHumanDateString(new Date(updatedAt))}
                      </Td>
                      <Td>{updatedByEmail}</Td>
                      <Td minWidth="20vw" whiteSpace="pre-wrap">
                        {description}
                      </Td>
                      <Td>
                        {isEditable ? (
                          <HStack spacing="24px">
                            <Button
                              variant="link"
                              color={colors.info}
                              onClick={() => {
                                navigate(
                                  `/settings/assignment-strategy/${id}/edit`,
                                );
                              }}
                            >
                              Edit
                            </Button>
                            <DeleteAssignmentStrategyButton
                              deleteAssignmentStrategy={async () => {
                                await deleteAssignmentStrategy({
                                  variables: { assignmentStrategyId: id },
                                });
                              }}
                              isLoading={deleteAssignmentStrategyLoading}
                            />
                          </HStack>
                        ) : (
                          <FiChevronRight color={colors.blue[400]} />
                        )}
                      </Td>
                    </Tr>
                  );
                },
              )
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Types
const RouteParamsStruct = z.object({
  implementationId: z.string({ message: 'Missing URL parameter' }),
});

export const AssignmentSelectStrategyContainer = () => {
  const {
    metadata,
  } = useLatestAssignmentPipelineRunMetadataForImplementationContext();

  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);

  const {
    data: assignmentStrategiesForImplementationData,
    loading: assignmentStrategiesForImplementationLoading,
    refetch: assignmentStrategiesForImplementationRefetch,
  } = useGetAssignmentStrategiesForImplementationQuery({
    variables: { implementationId },
    fetchPolicy: 'network-only',
  });

  return (
    <Box height="100%">
      {(() => {
        if (
          metadata === undefined ||
          !assignmentStrategiesForImplementationData ||
          assignmentStrategiesForImplementationLoading
        ) {
          return (
            <Flex direction="column">
              <Flex justify="space-between" mb="24px">
                <Heading as="h2">Select Strategy</Heading>
                <Spacer />
              </Flex>
              <AssignmentSkeletonLoadingTable
                numberOfRows={5}
                numberOfColumns={4}
              />
            </Flex>
          );
        }
        if (!metadata || metadata.status !== 'success') {
          return (
            <Flex direction="column">
              <Flex justify="space-between" mb="24px">
                <Heading as="h2">Select Strategy</Heading>
                <Spacer />
              </Flex>
              <PipelineStatusAlert
                status={metadata ? metadata.status : 'noPipelineRun'}
              />
            </Flex>
          );
        }

        return (
          <Box>
            <Flex direction="column">
              <Flex justify="space-between" mb="24px">
                <Heading as="h2">Select Strategy</Heading>
                <Spacer />
              </Flex>
              <Text fontSize="lg" mb="32px" color="gray">
                Select a strategy to apply; you&apos;ll have the opportunity to
                review it before running it.
              </Text>
            </Flex>
            <AssignmentSelectStrategyTable
              isEditable={false}
              assignmentStrategies={
                assignmentStrategiesForImplementationData.assignmentStrategiesForImplementation
              }
              refetchStrategies={() =>
                assignmentStrategiesForImplementationRefetch({
                  implementationId,
                })
              }
              implementationId={implementationId}
            />
          </Box>
        );
      })()}
    </Box>
  );
};
